/**
 * Space role permission catalog, toggle defs, and legacy normalization.
 *
 * Permissions are stored as flat boolean flags on each role. UI rows with a
 * 3-way toggle (No / Yes / Manage) map to a base + manage pair. Effective
 * permissions for a member are the additive union across all roles they hold.
 *
 * @module api/space-permissions
 */
import { isSpaceAdminRole } from './space-system-roles';
/** All Space permission flags (boolean, additive). */
export const SPACE_PERMISSIONS = [
    // general
    'viewChannels',
    'manageChannels',
    'manageRoles',
    'viewAuditLog',
    'manageWebhooks',
    'manageEncryption',
    'manageMetadata',
    'editProfile',
    'editTheme',
    'deleteSpace',
    // members
    'directInviteFriends',
    'directInviteAnyone',
    'createInviteLink',
    'manageInvites',
    'manageMembers',
    'changeNickname',
    'manageNicknames',
    'kickMembers',
    'banMembers',
    'manageMemberRoles',
    'manageApplications',
    // text
    'sendMessages',
    'manageMessages',
    'embedLinks',
    'attachFiles',
    'addReactions',
    'useCustomEmoji',
    'useGifsStickers',
    'mentionHere',
    'mentionRoles',
    'pinMessages',
    'bypassSlowmode',
    // voice
    'connect',
    'speak',
    'video',
    'stream',
    'muteMembers',
    // home
    'viewSpaceAvatar',
    'viewSpaceBio',
    'viewSpaceBanner',
    'manageAnnouncements',
    'manageActivity',
];
export const SPACE_PERMISSION_SET = new Set(SPACE_PERMISSIONS);
/** Categories shown in the Roles Manage Permissions tab. */
export const SPACE_PERMISSION_CATEGORIES = [
    'general',
    'home',
    'members',
    'text',
    'voice',
    'events',
];
/**
 * Categories whose flags only apply Space-wide. Channel and category
 * permission overrides never consult them, so override editors hide them.
 */
export const SPACE_ONLY_PERMISSION_CATEGORIES = ['home'];
/**
 * UI catalog. Events remain an empty shell for a later category.
 * Every stored flag appears exactly once (as `permission` or `managePermission`).
 */
export const SPACE_PERMISSION_DEFS = [
    // general
    { id: 'viewChannels', category: 'general', toggle: 'yesNo', permission: 'viewChannels' },
    { id: 'manageChannels', category: 'general', toggle: 'noManage', permission: 'manageChannels' },
    { id: 'manageRoles', category: 'general', toggle: 'noManage', permission: 'manageRoles' },
    { id: 'viewAuditLog', category: 'general', toggle: 'yesNo', permission: 'viewAuditLog' },
    { id: 'manageWebhooks', category: 'general', toggle: 'noManage', permission: 'manageWebhooks' },
    { id: 'manageEncryption', category: 'general', toggle: 'noManage', permission: 'manageEncryption' },
    { id: 'manageMetadata', category: 'general', toggle: 'noManage', permission: 'manageMetadata' },
    { id: 'editProfile', category: 'general', toggle: 'noManage', permission: 'editProfile' },
    { id: 'editTheme', category: 'general', toggle: 'noManage', permission: 'editTheme' },
    { id: 'deleteSpace', category: 'general', toggle: 'noManage', permission: 'deleteSpace' },
    // home
    { id: 'viewSpaceAvatar', category: 'home', toggle: 'yesNo', permission: 'viewSpaceAvatar' },
    { id: 'viewSpaceBio', category: 'home', toggle: 'yesNo', permission: 'viewSpaceBio' },
    { id: 'viewSpaceBanner', category: 'home', toggle: 'yesNo', permission: 'viewSpaceBanner' },
    {
        id: 'manageAnnouncements',
        category: 'home',
        toggle: 'noManage',
        permission: 'manageAnnouncements',
    },
    { id: 'manageActivity', category: 'home', toggle: 'noManage', permission: 'manageActivity' },
    // members
    {
        id: 'directInviteFriends',
        category: 'members',
        toggle: 'yesNo',
        permission: 'directInviteFriends',
    },
    {
        id: 'directInviteAnyone',
        category: 'members',
        toggle: 'yesNo',
        permission: 'directInviteAnyone',
    },
    { id: 'createInviteLink', category: 'members', toggle: 'yesNo', permission: 'createInviteLink' },
    { id: 'manageInvites', category: 'members', toggle: 'noManage', permission: 'manageInvites' },
    { id: 'manageMembers', category: 'members', toggle: 'noManage', permission: 'manageMembers' },
    {
        id: 'changeNickname',
        category: 'members',
        toggle: 'yesNoManage',
        permission: 'changeNickname',
        managePermission: 'manageNicknames',
    },
    { id: 'kickMembers', category: 'members', toggle: 'yesNo', permission: 'kickMembers' },
    { id: 'banMembers', category: 'members', toggle: 'yesNo', permission: 'banMembers' },
    {
        id: 'manageMemberRoles',
        category: 'members',
        toggle: 'yesNo',
        permission: 'manageMemberRoles',
    },
    {
        id: 'manageApplications',
        category: 'members',
        toggle: 'yesNo',
        permission: 'manageApplications',
    },
    // text
    {
        id: 'sendMessages',
        category: 'text',
        toggle: 'yesNoManage',
        permission: 'sendMessages',
        managePermission: 'manageMessages',
    },
    { id: 'embedLinks', category: 'text', toggle: 'yesNo', permission: 'embedLinks' },
    { id: 'attachFiles', category: 'text', toggle: 'yesNo', permission: 'attachFiles' },
    { id: 'addReactions', category: 'text', toggle: 'yesNo', permission: 'addReactions' },
    { id: 'useCustomEmoji', category: 'text', toggle: 'yesNo', permission: 'useCustomEmoji' },
    { id: 'useGifsStickers', category: 'text', toggle: 'yesNo', permission: 'useGifsStickers' },
    { id: 'mentionHere', category: 'text', toggle: 'yesNo', permission: 'mentionHere' },
    { id: 'mentionRoles', category: 'text', toggle: 'yesNo', permission: 'mentionRoles' },
    { id: 'pinMessages', category: 'text', toggle: 'yesNo', permission: 'pinMessages' },
    { id: 'bypassSlowmode', category: 'text', toggle: 'yesNo', permission: 'bypassSlowmode' },
    // voice
    { id: 'connect', category: 'voice', toggle: 'yesNo', permission: 'connect' },
    { id: 'speak', category: 'voice', toggle: 'yesNo', permission: 'speak' },
    { id: 'video', category: 'voice', toggle: 'yesNo', permission: 'video' },
    { id: 'stream', category: 'voice', toggle: 'yesNo', permission: 'stream' },
    { id: 'muteMembers', category: 'voice', toggle: 'yesNo', permission: 'muteMembers' },
];
/** Permissions that open the Space Manage shell. */
export const SPACE_MANAGE_UI_PERMISSIONS = [
    'manageMetadata',
    'manageRoles',
    'manageEncryption',
    'manageWebhooks',
    'viewAuditLog',
    'manageInvites',
    'manageMembers',
    'manageActivity',
];
/** Default Member role permissions. */
export const DEFAULT_MEMBER_PERMISSIONS = [
    'viewChannels',
    'sendMessages',
    'embedLinks',
    'attachFiles',
    'addReactions',
    'useCustomEmoji',
    'useGifsStickers',
    'changeNickname',
    'directInviteFriends',
    'connect',
    'speak',
    'video',
    'stream',
    'viewSpaceAvatar',
    'viewSpaceBio',
    'viewSpaceBanner',
];
/**
 * Who may see each part of the Space profile. Non-members are evaluated as the
 * Everyone role for these flags only.
 */
export const SPACE_PROFILE_VIEW_PERMISSIONS = [
    'viewSpaceAvatar',
    'viewSpaceBio',
    'viewSpaceBanner',
];
/**
 * Flags a permission always brings along. Applied by
 * {@link normalizeSpacePermissions}, so stored roles, escalation checks, and
 * toggles all agree. Removing the source flag leaves the implied ones set.
 */
export const SPACE_PERMISSION_IMPLICATIONS = {
    // Editors must be able to see what they edit.
    editProfile: SPACE_PROFILE_VIEW_PERMISSIONS,
};
/** Admin role gets every permission (no god-flag). */
export const DEFAULT_ADMIN_PERMISSIONS = [...SPACE_PERMISSIONS];
/** Default role colors (hex). */
export const DEFAULT_ADMIN_ROLE_COLOR = '#e74c3c';
export const DEFAULT_MEMBER_ROLE_COLOR = '#99aab5';
export const DEFAULT_CUSTOM_ROLE_COLOR = '#5865f2';
/**
 * Legacy permission strings still present on older role documents and on
 * stored channel/category permission overrides.
 *
 * `manageMembers` used to be a legacy alias for `kickMembers`. It is now a
 * catalog flag (Members directory + moderator notes); stored legacy roles are
 * rewritten once at startup by `backfillLegacyManageMembersRoles` so they keep
 * kick and do not silently gain the directory.
 *
 * `createInvite` predates the split into friends / anyone / link flags. It maps
 * to the narrowest capability (`directInviteFriends`) so a migration never
 * widens who an existing role can reach or hands out shareable links.
 */
const LEGACY_PERMISSION_MAP = {
    admin: SPACE_PERMISSIONS,
    read: ['viewChannels'],
    post: ['sendMessages'],
    invite: ['directInviteFriends'],
    createInvite: ['directInviteFriends'],
    manageRoles: ['manageRoles'],
};
/**
 * Normalize a raw permissions array (possibly legacy) into the current catalog.
 * Unknown strings are dropped. Order follows SPACE_PERMISSIONS.
 */
export function normalizeSpacePermissions(raw) {
    const out = new Set();
    for (const p of raw ?? []) {
        if (SPACE_PERMISSION_SET.has(p)) {
            out.add(p);
            continue;
        }
        const mapped = LEGACY_PERMISSION_MAP[p];
        if (mapped) {
            for (const m of mapped)
                out.add(m);
        }
    }
    for (const [source, implied] of Object.entries(SPACE_PERMISSION_IMPLICATIONS)) {
        if (out.has(source)) {
            for (const p of implied ?? [])
                out.add(p);
        }
    }
    return SPACE_PERMISSIONS.filter((p) => out.has(p));
}
/**
 * The held permission that forces this row on (e.g. `editProfile` for the
 * profile view rows), or `undefined` when the row is freely editable.
 */
export function spacePermissionImpliedBy(def, permissions) {
    const set = permissions instanceof Set ? permissions : new Set(permissions);
    for (const [source, implied] of Object.entries(SPACE_PERMISSION_IMPLICATIONS)) {
        if (set.has(source) && implied?.includes(def.permission)) {
            return source;
        }
    }
    return undefined;
}
/** Whether a permission list includes a specific flag (after normalize). */
export function spacePermissionListHas(permissions, permission) {
    return normalizeSpacePermissions(permissions).includes(permission);
}
/** Resolve the invite affordances for a permission list (post-normalization). */
export function resolveSpaceInviteCapabilities(permissions) {
    const set = new Set(normalizeSpacePermissions(permissions));
    const inviteFriends = set.has('directInviteFriends');
    const inviteAnyone = set.has('directInviteAnyone');
    const createLink = set.has('createInviteLink');
    return {
        inviteFriends,
        inviteAnyone,
        createLink,
        manageInvites: set.has('manageInvites'),
        canInvite: inviteFriends || inviteAnyone || createLink,
    };
}
/**
 * Whether the actor may send a direct invite to a specific identity.
 *
 * `directInviteAnyone` subsumes `directInviteFriends`: holding the broader flag
 * alone is enough to invite a friend. A friends-only actor needs an existing
 * mutual friendship.
 *
 * Callers must not surface *why* this returned false — a distinct
 * "not friends" error would confirm whether two identities are friends to
 * anyone who can invite. Collapse it into the generic not-allowed response.
 */
export function canDirectInviteIdentity(params) {
    const caps = resolveSpaceInviteCapabilities(params.permissions);
    if (caps.inviteAnyone)
        return true;
    return caps.inviteFriends && params.isFriend;
}
/** Whether the viewer can open the Space Manage shell. */
export function canAccessSpaceManageUi(permissions) {
    const normalized = normalizeSpacePermissions(permissions);
    return SPACE_MANAGE_UI_PERMISSIONS.some((p) => normalized.includes(p));
}
/** Read the effective toggle value for a permission def from a flag set. */
export function getSpacePermissionToggleValue(def, permissions) {
    const set = permissions instanceof Set
        ? permissions
        : new Set(normalizeSpacePermissions(permissions));
    if (def.toggle === 'yesNoManage') {
        if (def.managePermission && set.has(def.managePermission))
            return 'manage';
        if (set.has(def.permission))
            return 'yes';
        return 'no';
    }
    if (def.toggle === 'noManage') {
        return set.has(def.permission) ? 'manage' : 'no';
    }
    return set.has(def.permission) ? 'yes' : 'no';
}
/**
 * Apply a toggle value to a permission set for one def.
 * Returns a new sorted permission array.
 */
export function applySpacePermissionToggle(current, def, value) {
    const set = new Set(normalizeSpacePermissions(current));
    const clear = () => {
        set.delete(def.permission);
        if (def.managePermission)
            set.delete(def.managePermission);
    };
    if (def.toggle === 'yesNoManage') {
        clear();
        if (value === 'yes') {
            set.add(def.permission);
        }
        else if (value === 'manage') {
            set.add(def.permission);
            if (def.managePermission)
                set.add(def.managePermission);
        }
    }
    else if (def.toggle === 'noManage') {
        if (value === 'manage' || value === 'yes') {
            set.add(def.permission);
        }
        else {
            set.delete(def.permission);
        }
    }
    else {
        if (value === 'yes' || value === 'manage') {
            set.add(def.permission);
        }
        else {
            set.delete(def.permission);
        }
    }
    return normalizeSpacePermissions([...set]);
}
/** Segment options for a given toggle kind. */
export function spacePermissionToggleOptions(kind) {
    if (kind === 'yesNoManage')
        return ['no', 'yes', 'manage'];
    if (kind === 'noManage')
        return ['no', 'manage'];
    return ['no', 'yes'];
}
/** Whether `granted` is a subset of `holder` (escalation check). */
export function spacePermissionsSubsetOf(granted, holder) {
    const holderSet = new Set(normalizeSpacePermissions(holder));
    return normalizeSpacePermissions(granted).every((p) => holderSet.has(p));
}
/**
 * Resolve one role's effective permissions through the override cascade.
 *
 * Order: space role → category overrides (root → leaf) → channel override.
 * Each present override fully replaces the set for that role.
 */
export function resolveRolePermissionsThroughOverrides(params) {
    let current = normalizeSpacePermissions(params.spaceRolePermissions);
    for (const override of params.ancestorOverridesOldestFirst) {
        if (override != null) {
            current = normalizeSpacePermissions(override);
        }
    }
    if (params.channelOverride != null) {
        current = normalizeSpacePermissions(params.channelOverride);
    }
    return current;
}
/**
 * Look up a role's override permissions from a node override list.
 * Returns `undefined` when the role has no entry (inherit parent).
 */
export function findRolePermissionOverride(overrides, roleId) {
    if (!overrides?.length)
        return undefined;
    const entry = overrides.find((o) => o.roleId === roleId);
    if (!entry)
        return undefined;
    return normalizeSpacePermissions(entry.permissions);
}
/**
 * Normalize a raw override list: drop invalid role ids / empty, de-dupe by
 * roleId (last wins), normalize permission catalogs.
 */
export function normalizeRolePermissionOverrides(raw) {
    if (!raw?.length)
        return [];
    const byRole = new Map();
    for (const entry of raw) {
        const roleId = typeof entry.roleId === 'string' ? entry.roleId.trim() : '';
        if (!/^[0-9a-fA-F]{24}$/.test(roleId))
            continue;
        byRole.set(roleId, normalizeSpacePermissions(entry.permissions));
    }
    return [...byRole.entries()].map(([roleId, permissions]) => ({ roleId, permissions }));
}
/**
 * Whether the actor may newly grant a specific Space role to a member.
 *
 * - System Admin: only system Admins may assign it.
 * - `manageRoles`: any other role.
 * - `manageMemberRoles` only: role permissions must be ⊆ actor's.
 */
export function canGrantSpaceMemberRole(params) {
    const { role, actorPermissions, actorIsAdmin, actorCanManageRoles } = params;
    if (isSpaceAdminRole(role))
        return actorIsAdmin;
    if (actorCanManageRoles)
        return true;
    return spacePermissionsSubsetOf(role.permissions, actorPermissions);
}
//# sourceMappingURL=space-permissions.js.map